import { Box, Flex, Text } from '@chakra-ui/react';
import { faCube } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, Button, Modal } from '@morressier/alembic';
import { EmailTemplateDoc } from '@publishing-engine/domain/models/email-templates';
import { useState } from 'react';
import PreviewEmail from './PreviewEmail';

interface Props {
  onClose: () => void;
  templates: EmailTemplateDoc[];
  onSelectTemplate: (index: number) => void;
  defaultIndex: number | null;
  isCustomTemplate: boolean;
}

const BrowseTemplates = ({
  onClose,
  templates,
  onSelectTemplate,
  defaultIndex,
  isCustomTemplate
}: Props) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(
    defaultIndex
  );

  const handleSelect = () => {
    if (selectedIndex !== null) {
      onSelectTemplate(selectedIndex);
    }
  };

  const selectedTemplate =
    selectedIndex !== null ? templates[selectedIndex] : null;

  return (
    <Modal
      isOpen={true}
      onClose={() => {}}
      width="80%"
      closeButtonProps={{ hidden: true }}
    >
      <Box backgroundColor="surfaceSecondaryFill">
        <Flex flexDirection="column" padding={4}>
          <Text textStyle="headingGroup">Browse Templates</Text>
          <Text textStyle="headingOverline">
            {isCustomTemplate
              ? 'Select a template from the options that best fits your needs for the following events. Each template is tailored for different use case, so review their features to find the right match.'
              : ` Please choose a template from the options provided that best aligns
            with your specific requirements for the upcoming events. Each
            template is designed to cater to different types of events, so take
            a moment to review the features and details of each one to ensure it
            meets your needs effectively.`}
          </Text>
        </Flex>

        <Flex gap={4} padding={4} alignItems="flex-start">
          <Flex flexDirection="column" gap={4} flex="0 0 25%">
            <Text textStyle="headingGroup">
              Event Templates ({templates.length})
            </Text>
            <Flex flexDirection="column" gap={4}>
              {templates.map((template, index) => (
                <Box
                  key={index}
                  borderWidth="1px"
                  borderStyle="solid"
                  borderColor={
                    selectedIndex === index
                      ? 'actionSecondaryContent'
                      : 'neutralSecondaryBorder'
                  }
                  borderRadius="small"
                  padding={2}
                  cursor="pointer"
                  onClick={() => setSelectedIndex(index)}
                >
                  <Text
                    textStyle="headingItem"
                    color={
                      selectedIndex === index ? 'actionSecondaryContent' : ''
                    }
                  >
                    {template.name}
                  </Text>
                  {template.default && <Badge variant="neutral">Default</Badge>}
                  <Text textStyle="textContext">{template.description}</Text>
                </Box>
              ))}
            </Flex>
          </Flex>

          <Flex
            flexDirection="column"
            padding={4}
            gap={4}
            backgroundColor="surfacePrimaryFill"
            flex={1}
            minWidth="0"
          >
            {selectedTemplate ? (
              <>
                <Text textStyle="headingGroup">
                  {isCustomTemplate
                    ? `${selectedTemplate.name}`
                    : 'Invitation Template'}
                </Text>
                <Flex gap={2}>
                  <Text textStyle="headingItem">Template ID:</Text>
                  <Text textStyle="textBody">{`#${selectedTemplate._id}`}</Text>
                </Flex>
                {selectedTemplate.description ? (
                  <Flex flexDirection="column" gap={2}>
                    <Text textStyle="headingItem">Description:</Text>
                    <Text textStyle="textBody">
                      {selectedTemplate.description}
                    </Text>
                  </Flex>
                ) : null}
                <PreviewEmail template={selectedTemplate} />
              </>
            ) : (
              <Flex
                flexDirection="column"
                textAlign="center"
                gap={4}
                minH={'400px'}
              >
                <Box bgColor="neutralSecondaryFill" padding={8}>
                  <FontAwesomeIcon icon={faCube} fontSize={40} />
                </Box>
                <Flex flexDirection="column" gap={2}>
                  <Text textStyle="headingGroup">No Template Selected</Text>
                  <Text textStyle="textBody" paddingX={2}>
                    Choose the template  you would like to proceed with from the list
                    on the left that corresponds to your event.
                  </Text>
                </Flex>
              </Flex>
            )}
          </Flex>
        </Flex>

        <Flex
          backgroundColor="surfacePrimaryFill"
          justifyContent="flex-end"
          gap={4}
          padding={4}
          marginBottom={!isCustomTemplate ? 4 : 0}
        >
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={selectedTemplate === null}
            onClick={handleSelect}
          >
            Select Template
          </Button>
        </Flex>
      </Box>
    </Modal>
  );
};

export default BrowseTemplates;
